// Coupons Service - Realtime Database operations
import { ref, get, update, remove, set, child, onValue, off, push } from 'firebase/database'
import { database } from './firebase'
import { getAllStores } from './usersService'
import {
  HIGH_IMPACT_DELETIONS_DISABLED,
  getHighImpactDeletionBlock,
} from '@/utils/highImpactDeletionGuard'
import {
  buildAuditEntry,
  buildProductAuditEntry,
  buildRenameChanges,
  computeCouponChanges,
  extractProductAuditInfo,
  extractProductCode,
  normalizeCouponForAudit,
  validateCouponActor,
} from '@/utils/couponAudit'
import { CSV_MAX_COUPONS } from '@/utils/couponCsv'

// Base path for coupons in Firebase Realtime Database
const COUPONS_BASE_PATH = '/projects/superkomprasBackoffice/coupons'

/**
 * Append an audit log entry and update coupon metadata.
 * @param {string} couponName
 * @param {Object} entry
 */
const appendCouponAuditEntry = async (couponName, entry) => {
  const couponRef = ref(database, `${COUPONS_BASE_PATH}/${couponName}`)
  const auditLogRef = push(ref(database, `${COUPONS_BASE_PATH}/${couponName}/auditLog`))

  await update(couponRef, {
    updatedAt: entry.at,
    updatedBy: entry.by,
    [`auditLog/${auditLogRef.key}`]: entry,
  })
}

/**
 * Get all coupons from Realtime Database
 * @returns {Promise<Object>} All coupons data
 */
export const getAllCoupons = async () => {
  try {
    const couponsRef = ref(database, COUPONS_BASE_PATH)
    const snapshot = await get(couponsRef)

    if (snapshot.exists()) {
      const couponsData = snapshot.val()
      // Convert object to array with coupon name as key
      const couponsArray = Object.keys(couponsData).map(key => ({
        nombre: key,
        ...couponsData[key],
        rtdbKey: key,
      }))

      return {
        success: true,
        data: couponsArray,
      }
    } else {
      return {
        success: true,
        data: [],
      }
    }
  } catch (error) {
    console.error('Error in getAllCoupons:', error)
    return {
      success: false,
      error: error.message || 'Error al obtener cupones',
      data: [],
    }
  }
}

/**
 * Get coupon by name from Realtime Database
 * @param {string} couponName - Coupon name (key)
 * @returns {Promise<Object>} Coupon data or null if not found
 */
export const getCouponByName = async couponName => {
  try {
    const couponRef = ref(database, `${COUPONS_BASE_PATH}/${couponName}`)
    const snapshot = await get(couponRef)

    if (snapshot.exists()) {
      return {
        success: true,
        data: {
          nombre: couponName,
          ...snapshot.val(),
        },
      }
    } else {
      return {
        success: false,
        error: 'Cupón no encontrado',
        data: null,
      }
    }
  } catch (error) {
    console.error('Error in getCouponByName:', error)
    return {
      success: false,
      error: error.message || 'Error al obtener cupón',
      data: null,
    }
  }
}

/**
 * Check if a coupon name already exists
 * @param {string} couponName - Coupon name to check
 * @param {string} excludeName - Optional name to exclude from check (for editing)
 * @returns {Promise<boolean>} True if exists, false otherwise
 */
export const couponNameExists = async (couponName, excludeName = null) => {
  try {
    if (excludeName && couponName === excludeName) {
      return false // Same name, allowed for editing
    }

    const couponRef = ref(database, `${COUPONS_BASE_PATH}/${couponName}`)
    const snapshot = await get(couponRef)
    return snapshot.exists()
  } catch (error) {
    console.error('Error in couponNameExists:', error)
    return false
  }
}

/**
 * Arma el nodo que se persiste al crear un cupón, tanto en el alta individual
 * como en el lote CSV.
 * @param {Object} couponData
 * @param {Object} actor
 * @param {number} now
 * @param {string} auditKey
 * @returns {Object}
 */
const buildCreatedCouponRecord = (couponData, actor, now, auditKey) => {
  const nombre = couponData.nombre.trim()
  const couponToSave = {
    nombre,
    tipoDescuento: couponData.tipoDescuento || 'carrito',
    descuentoCompra: String(couponData.descuentoCompra || '0'),
    descuentoEnvio: String(couponData.descuentoEnvio || '0'),
    cantidadDeCanjesPorUsuario: String(couponData.cantidadDeCanjesPorUsuario || '0'),
    cantidadDeCanjesTotal: String(couponData.cantidadDeCanjesTotal || '0'),
    fechaInicio: String(couponData.fechaInicio || now),
    fechaFin: String(couponData.fechaFin || now),
    minimoDeCompra: String(couponData.minimoDeCompra || '0'),
    sucursal: String(couponData.sucursal || '0'),
    CuponesCanjeados: couponData.CuponesCanjeados || {},
  }

  return {
    ...couponToSave,
    createdAt: now,
    createdBy: actor,
    updatedAt: now,
    updatedBy: actor,
    auditLog: {
      [auditKey]: buildAuditEntry(
        'created',
        actor,
        null,
        normalizeCouponForAudit(couponToSave),
        now
      ),
    },
  }
}

/**
 * Nombres de cupón en la colección actual.
 * Un error de lectura no se reporta como lista vacía.
 * @returns {Promise<{ success: boolean, names: string[] | null, error?: string }>}
 */
export const listCouponNames = async () => {
  try {
    const couponsRef = ref(database, COUPONS_BASE_PATH)
    const snapshot = await get(couponsRef)

    if (!snapshot.exists()) {
      return { success: true, names: [] }
    }

    const couponsData = snapshot.val()
    if (!couponsData || typeof couponsData !== 'object') {
      return { success: true, names: [] }
    }

    return { success: true, names: Object.keys(couponsData) }
  } catch (error) {
    console.error('Error in listCouponNames:', error)
    return {
      success: false,
      error: error.message || 'No se pudo verificar si los cupones ya existen',
      names: null,
    }
  }
}

/**
 * Create a new coupon in Realtime Database
 * @param {Object} couponData - Coupon data
 * @param {Object} actor - Usuario autenticado (uid, email, name)
 * @returns {Promise<Object>} Result of create operation
 */
export const createCoupon = async (couponData, actor) => {
  try {
    const actorValidation = validateCouponActor(actor)
    if (!actorValidation.valid) {
      return {
        success: false,
        error: actorValidation.error,
      }
    }

    const normalizedActor = actorValidation.actor
    const { nombre, ...restData } = couponData

    // Validate that name is provided
    if (!nombre || nombre.trim() === '') {
      return {
        success: false,
        error: 'El nombre del cupón es requerido',
      }
    }

    // Check if coupon name already exists
    const exists = await couponNameExists(nombre)
    if (exists) {
      return {
        success: false,
        error: `Ya existe un cupón con el nombre "${nombre}"`,
      }
    }

    const trimmedName = nombre.trim()
    const now = Date.now()
    const auditLogRef = push(ref(database, `${COUPONS_BASE_PATH}/${trimmedName}/auditLog`))
    const couponRef = ref(database, `${COUPONS_BASE_PATH}/${trimmedName}`)
    await set(
      couponRef,
      buildCreatedCouponRecord(
        { nombre: trimmedName, ...restData },
        normalizedActor,
        now,
        auditLogRef.key
      )
    )

    return {
      success: true,
      message: 'Cupón creado correctamente',
    }
  } catch (error) {
    console.error('Error in createCoupon:', error)
    return {
      success: false,
      error: error.message || 'Error al crear cupón',
    }
  }
}

/**
 * Crea varios cupones con un único update() sobre la colección vigente.
 * Si la lectura de existentes falla, o cualquier nombre ya existe, no escribe.
 *
 * La segunda lectura ocurre aquí, inmediatamente antes del update. Eso reduce
 * la ventana en la que otro cliente puede crear la misma clave, pero no la
 * cierra: las rules actuales no exigen !data.exists() y RTDB no condiciona
 * este update a la ausencia del nodo.
 *
 * @param {Object[]} drafts - Borradores ya validados
 * @param {Object} actor
 * @returns {Promise<Object>}
 */
export const createCouponsBatch = async (drafts, actor) => {
  try {
    const actorValidation = validateCouponActor(actor)
    if (!actorValidation.valid) {
      return {
        success: false,
        error: actorValidation.error,
      }
    }

    if (!Array.isArray(drafts) || drafts.length === 0) {
      return {
        success: false,
        error: 'No hay cupones para crear',
      }
    }

    if (drafts.length > CSV_MAX_COUPONS) {
      return {
        success: false,
        error: `El lote supera el máximo de ${CSV_MAX_COUPONS} cupones`,
      }
    }

    const names = drafts.map(draft => (draft && draft.nombre ? draft.nombre.trim() : ''))
    if (names.some(name => !name)) {
      return {
        success: false,
        error: 'El lote contiene un cupón sin nombre',
      }
    }

    const seen = new Set()
    const duplicated = []
    names.forEach(name => {
      if (seen.has(name)) duplicated.push(name)
      seen.add(name)
    })
    if (duplicated.length > 0) {
      return {
        success: false,
        error: `Nombres duplicados en el lote: ${[...new Set(duplicated)].join(', ')}`,
      }
    }

    const existingResult = await listCouponNames()
    if (!existingResult.success) {
      return {
        success: false,
        error: existingResult.error || 'No se pudo verificar si los cupones ya existen',
      }
    }

    const existing = new Set(existingResult.names || [])
    const conflicts = names.filter(name => existing.has(name))
    if (conflicts.length > 0) {
      return {
        success: false,
        error: `Ya existen cupones con estos nombres: ${conflicts.join(', ')}`,
        existingNames: conflicts,
      }
    }

    const normalizedActor = actorValidation.actor
    const now = Date.now()
    const updates = {}
    drafts.forEach(draft => {
      const nombre = draft.nombre.trim()
      const auditLogRef = push(ref(database, `${COUPONS_BASE_PATH}/${nombre}/auditLog`))
      updates[nombre] = buildCreatedCouponRecord(
        { ...draft, nombre, CuponesCanjeados: {} },
        normalizedActor,
        now,
        auditLogRef.key
      )
    })

    await update(ref(database, COUPONS_BASE_PATH), updates)

    return {
      success: true,
      message: 'Cupones creados correctamente',
      created: drafts.length,
    }
  } catch (error) {
    console.error('Error in createCouponsBatch:', error)
    return {
      success: false,
      error: error.message || 'Error al crear cupones',
    }
  }
}

/**
 * Update an existing coupon in Realtime Database
 * @param {string} couponName - Current coupon name (key)
 * @param {Object} couponData - Updated coupon data
 * @param {Object} actor - Usuario autenticado (uid, email, name)
 * @returns {Promise<Object>} Result of update operation
 */
export const updateCoupon = async (couponName, couponData, actor) => {
  try {
    const actorValidation = validateCouponActor(actor)
    if (!actorValidation.valid) {
      return {
        success: false,
        error: actorValidation.error,
      }
    }

    const normalizedActor = actorValidation.actor

    // Check if coupon exists
    const existingCoupon = await getCouponByName(couponName)
    if (!existingCoupon.success || !existingCoupon.data) {
      return {
        success: false,
        error: 'Cupón no encontrado',
      }
    }

    // If name is changing, check if new name exists
    if (couponData.nombre && couponData.nombre !== couponName) {
      const nameExists = await couponNameExists(couponData.nombre, couponName)
      if (nameExists) {
        return {
          success: false,
          error: `Ya existe un cupón con el nombre "${couponData.nombre}"`,
        }
      }

      const newName = couponData.nombre.trim()
      const { nombre, CuponesCanjeados, ...updateData } = couponData
      const existing = existingCoupon.data
      const beforeState = normalizeCouponForAudit(existing)
      const afterState = normalizeCouponForAudit({ ...existing, ...updateData, nombre: newName })
      const changes = buildRenameChanges(couponName, newName, beforeState, afterState)
      const now = Date.now()

      const auditLogRef = push(ref(database, `${COUPONS_BASE_PATH}/${newName}/auditLog`))
      const preservedAuditLog = existing.auditLog || {}
      const mergedAuditLog = {
        ...preservedAuditLog,
        [auditLogRef.key]: buildAuditEntry('renamed', normalizedActor, changes, null, now),
      }

      const couponToSave = {
        nombre: newName,
        tipoDescuento: updateData.tipoDescuento || existing.tipoDescuento,
        descuentoCompra: String(
          updateData.descuentoCompra ?? existing.descuentoCompra ?? '0'
        ),
        descuentoEnvio: String(
          updateData.descuentoEnvio ?? existing.descuentoEnvio ?? '0'
        ),
        cantidadDeCanjesPorUsuario: String(
          updateData.cantidadDeCanjesPorUsuario ??
            existing.cantidadDeCanjesPorUsuario ??
            '0'
        ),
        cantidadDeCanjesTotal: String(
          updateData.cantidadDeCanjesTotal ?? existing.cantidadDeCanjesTotal ?? '0'
        ),
        fechaInicio: String(updateData.fechaInicio ?? existing.fechaInicio),
        fechaFin: String(updateData.fechaFin ?? existing.fechaFin),
        minimoDeCompra: String(
          updateData.minimoDeCompra ?? existing.minimoDeCompra ?? '0'
        ),
        sucursal: String(updateData.sucursal ?? existing.sucursal ?? '0'),
        CuponesCanjeados: existing.CuponesCanjeados || {},
        createdAt: existing.createdAt ?? null,
        createdBy: existing.createdBy ?? null,
        updatedAt: now,
        updatedBy: normalizedActor,
        auditLog: mergedAuditLog,
      }

      if (existing.products) {
        couponToSave.products = existing.products
      }

      const newCouponRef = ref(database, `${COUPONS_BASE_PATH}/${newName}`)
      await set(newCouponRef, couponToSave)

      const oldCouponRef = ref(database, `${COUPONS_BASE_PATH}/${couponName}`)
      await remove(oldCouponRef)

      return {
        success: true,
        message: 'Cupón actualizado correctamente',
        renamed: true,
        newName,
      }
    } else {
      // Name not changing, just update fields
      const { nombre, CuponesCanjeados, ...updateData } = couponData
      const existing = existingCoupon.data
      const beforeState = normalizeCouponForAudit(existing)
      const afterState = normalizeCouponForAudit({ ...existing, ...updateData })
      const changes = computeCouponChanges(beforeState, afterState)

      if (Object.keys(changes).length === 0) {
        return {
          success: true,
          message: 'Cupón actualizado correctamente',
        }
      }

      const now = Date.now()
      const updates = {
        updatedAt: now,
        updatedBy: normalizedActor,
      }

      if (updateData.tipoDescuento !== undefined) {
        updates.tipoDescuento = updateData.tipoDescuento
      }
      if (updateData.descuentoCompra !== undefined) {
        updates.descuentoCompra = String(updateData.descuentoCompra)
      }
      if (updateData.descuentoEnvio !== undefined) {
        updates.descuentoEnvio = String(updateData.descuentoEnvio)
      }
      if (updateData.cantidadDeCanjesPorUsuario !== undefined) {
        updates.cantidadDeCanjesPorUsuario = String(updateData.cantidadDeCanjesPorUsuario)
      }
      if (updateData.cantidadDeCanjesTotal !== undefined) {
        updates.cantidadDeCanjesTotal = String(updateData.cantidadDeCanjesTotal)
      }
      if (updateData.fechaInicio !== undefined) {
        updates.fechaInicio = String(updateData.fechaInicio)
      }
      if (updateData.fechaFin !== undefined) {
        updates.fechaFin = String(updateData.fechaFin)
      }
      if (updateData.minimoDeCompra !== undefined) {
        updates.minimoDeCompra = String(updateData.minimoDeCompra)
      }
      if (updateData.sucursal !== undefined) {
        updates.sucursal = String(updateData.sucursal)
      }

      const auditLogRef = push(ref(database, `${COUPONS_BASE_PATH}/${couponName}/auditLog`))
      updates[`auditLog/${auditLogRef.key}`] = buildAuditEntry(
        'updated',
        normalizedActor,
        changes,
        null,
        now
      )

      const couponRef = ref(database, `${COUPONS_BASE_PATH}/${couponName}`)
      await update(couponRef, updates)

      return {
        success: true,
        message: 'Cupón actualizado correctamente',
      }
    }
  } catch (error) {
    console.error('Error in updateCoupon:', error)
    return {
      success: false,
      error: error.message || 'Error al actualizar cupón',
    }
  }
}

const STALE_VALIDITY_ERROR =
  'Los datos de vigencia cambiaron desde la validación. Vuelve a cargar el archivo para validar de nuevo. No se actualizó ningún cupón.'

/**
 * Actualiza solo las vigencias modificadas con un único update() multipath.
 * Relee la colección y compara fechaInicio/fechaFin antes de escribir.
 * La ventana entre esa lectura y el update no queda cerrada.
 *
 * @param {Array<{ key: string, originalFechaInicio: string|number, originalFechaFin: string|number, fechaInicio: string|number, fechaFin: string|number }>} items
 * @param {Object} actor
 */
export const updateCouponValidityBatch = async (items, actor) => {
  try {
    const actorValidation = validateCouponActor(actor)
    if (!actorValidation.valid) {
      return { success: false, error: actorValidation.error }
    }
    const normalizedActor = actorValidation.actor

    if (!Array.isArray(items) || items.length === 0) {
      return { success: false, error: 'No hay cupones para actualizar' }
    }
    if (items.length > CSV_MAX_COUPONS) {
      return {
        success: false,
        error: `El lote supera el máximo de ${CSV_MAX_COUPONS} cupones`,
      }
    }

    const keys = items.map(item => (item && item.key ? String(item.key).trim() : ''))
    if (keys.some(key => !key)) {
      return { success: false, error: 'El lote contiene un cupón sin nombre' }
    }
    if (new Set(keys).size !== keys.length) {
      return { success: false, error: 'El lote contiene nombres duplicados' }
    }
    if (
      items.some(item => {
        const inicio = Number(item.fechaInicio)
        const fin = Number(item.fechaFin)
        return !Number.isFinite(inicio) || !Number.isFinite(fin) || fin <= inicio
      })
    ) {
      return {
        success: false,
        error: 'El lote contiene vigencias inválidas. No se actualizó ningún cupón.',
      }
    }

    const snapshot = await get(ref(database, COUPONS_BASE_PATH))
    const current = snapshot.exists() ? snapshot.val() || {} : {}
    const normalizedItems = items.map((item, index) => ({ ...item, key: keys[index] }))

    for (const item of normalizedItems) {
      const node = current[item.key]
      if (!node) {
        return { success: false, error: STALE_VALIDITY_ERROR }
      }
      const sameInicio = Number(node.fechaInicio) === Number(item.originalFechaInicio)
      const sameFin = Number(node.fechaFin) === Number(item.originalFechaFin)
      if (!sameInicio || !sameFin) {
        return { success: false, error: STALE_VALIDITY_ERROR }
      }
    }

    const now = Date.now()
    const updates = {}
    normalizedItems.forEach(item => {
      const changes = computeCouponChanges(
        normalizeCouponForAudit({
          fechaInicio: item.originalFechaInicio,
          fechaFin: item.originalFechaFin,
        }),
        normalizeCouponForAudit({
          fechaInicio: item.fechaInicio,
          fechaFin: item.fechaFin,
        })
      )
      const changedFields = Object.keys(changes)
      if (changedFields.length === 0) return

      if (changes.fechaInicio) updates[`${item.key}/fechaInicio`] = String(item.fechaInicio)
      if (changes.fechaFin) updates[`${item.key}/fechaFin`] = String(item.fechaFin)
      updates[`${item.key}/updatedAt`] = now
      updates[`${item.key}/updatedBy`] = normalizedActor
      const auditLogRef = push(ref(database, `${COUPONS_BASE_PATH}/${item.key}/auditLog`))
      updates[`${item.key}/auditLog/${auditLogRef.key}`] = buildAuditEntry(
        'updated',
        normalizedActor,
        changes,
        null,
        now
      )
    })

    if (Object.keys(updates).length === 0) {
      return { success: true, updated: 0 }
    }

    await update(ref(database, COUPONS_BASE_PATH), updates)
    return {
      success: true,
      updated: normalizedItems.filter(item => updates[`${item.key}/updatedAt`] !== undefined).length,
    }
  } catch (error) {
    console.error('Error in updateCouponValidityBatch:', error)
    return {
      success: false,
      error: error.message || 'Error al actualizar vigencias',
    }
  }
}

/**
 * Lee las vigencias indexadas por la key real del nodo, no por la propiedad nombre.
 */
export const getCouponValidityCatalog = async () => {
  try {
    const snapshot = await get(ref(database, COUPONS_BASE_PATH))
    if (!snapshot.exists()) {
      return { success: true, catalog: {} }
    }
    const data = snapshot.val() || {}
    const catalog = {}
    Object.keys(data).forEach(key => {
      const node = data[key] || {}
      catalog[key] = {
        fechaInicio: node.fechaInicio ?? '',
        fechaFin: node.fechaFin ?? '',
      }
    })
    return { success: true, catalog }
  } catch (error) {
    console.error('Error in getCouponValidityCatalog:', error)
    return {
      success: false,
      catalog: null,
      error: error.message || 'No se pudieron leer las vigencias',
    }
  }
}


/**
 * Delete a coupon from Realtime Database
 * @param {string} couponName - Coupon name (key) to delete
 * @returns {Promise<Object>} Result of delete operation
 */
export const deleteCoupon = async couponName => {
  try {
    if (HIGH_IMPACT_DELETIONS_DISABLED) {
      return getHighImpactDeletionBlock('eliminar cupón')
    }

    // Check if coupon exists
    const existingCoupon = await getCouponByName(couponName)
    if (!existingCoupon.success || !existingCoupon.data) {
      return {
        success: false,
        error: 'Cupón no encontrado',
      }
    }

    const couponRef = ref(database, `${COUPONS_BASE_PATH}/${couponName}`)
    await remove(couponRef)

    return {
      success: true,
      message: 'Cupón eliminado correctamente',
    }
  } catch (error) {
    console.error('Error in deleteCoupon:', error)
    return {
      success: false,
      error: error.message || 'Error al eliminar cupón',
    }
  }
}

/**
 * Get redeemed coupons for a specific coupon
 * @param {string} couponName - Coupon name
 * @returns {Promise<Object>} Redeemed coupons data
 */
export const getRedeemedCoupons = async couponName => {
  try {
    const redeemedRef = ref(database, `${COUPONS_BASE_PATH}/${couponName}/CuponesCanjeados`)
    const snapshot = await get(redeemedRef)

    if (snapshot.exists()) {
      const redeemedData = snapshot.val()
      // Convert object to array, where each key is a user ID
      const redeemedArray = Object.keys(redeemedData).map(userId => {
        const userData = redeemedData[userId]
        return {
          id: userId,
          nombre: userData.nombre || userData.name || 'N/A',
          correo: userData.correo || userData.email || 'N/A',
          telefono: userData.telefono || userData.phone || userData.telef || 'N/A',
          vecesCanjeadas: userData.vecesCanjeadas || userData.timesRedeemed || 0,
        }
      })

      return {
        success: true,
        data: redeemedArray,
      }
    } else {
      return {
        success: true,
        data: [],
      }
    }
  } catch (error) {
    console.error('Error in getRedeemedCoupons:', error)
    return {
      success: false,
      error: error.message || 'Error al obtener cupones canjeados',
      data: [],
    }
  }
}

/**
 * Get all stores for the sucursal selector
 * @returns {Promise<Object>} Stores data
 */
export const getStoresForSelector = async () => {
  try {
    const result = await getAllStores()
    if (result.success && result.data && Array.isArray(result.data)) {
      // Filter out any invalid stores and ensure they have division and sucursal
      const validStores = result.data.filter(
        store =>
          store &&
          typeof store === 'object' &&
          store.division !== undefined &&
          store.division !== null &&
          store.division !== ''
      )

      // Add "TODAS LAS SUCURSALES" option at the beginning
      const stores = [{ division: '0', sucursal: 'TODAS LAS SUCURSALES' }, ...validStores]
      return {
        success: true,
        data: stores,
      }
    }
    return {
      success: true,
      data: [{ division: '0', sucursal: 'TODAS LAS SUCURSALES' }],
    }
  } catch (error) {
    console.error('Error in getStoresForSelector:', error)
    return {
      success: true,
      data: [{ division: '0', sucursal: 'TODAS LAS SUCURSALES' }],
    }
  }
}

/**
 * Get all products for a specific coupon
 * @param {string} couponName - Coupon name
 * @returns {Promise<Object>} Products data
 */
export const getCouponProducts = async couponName => {
  try {
    const productsRef = ref(database, `${COUPONS_BASE_PATH}/${couponName}/products`)
    const snapshot = await get(productsRef)

    if (snapshot.exists()) {
      const productsData = snapshot.val()
      // Convert object to array with CODIGO as key
      const productsArray = Object.keys(productsData).map(codigo => ({
        CODIGO: codigo,
        ...productsData[codigo],
      }))

      return {
        success: true,
        data: productsArray,
      }
    } else {
      return {
        success: true,
        data: [],
      }
    }
  } catch (error) {
    console.error('Error in getCouponProducts:', error)
    return {
      success: false,
      error: error.message || 'Error al obtener productos del cupón',
      data: [],
    }
  }
}

/**
 * Add a product to a coupon
 * @param {string} couponName - Coupon name
 * @param {Object} product - Product object from Algolia (full object)
 * @param {Object} actor - Usuario autenticado (uid, email, name)
 * @returns {Promise<Object>} Result of add operation
 */
export const addProductToCoupon = async (couponName, product, actor) => {
  try {
    const actorValidation = validateCouponActor(actor)
    if (!actorValidation.valid) {
      return {
        success: false,
        error: actorValidation.error,
      }
    }

    if (!couponName || !product) {
      return {
        success: false,
        error: 'Cupón y producto son requeridos',
      }
    }

    const normalizedActor = actorValidation.actor
    const codigo = extractProductCode(product)

    if (!codigo) {
      return {
        success: false,
        error: 'El producto no tiene un código válido (CODIGO)',
      }
    }

    // Check if coupon exists
    const existingCoupon = await getCouponByName(couponName)
    if (!existingCoupon.success || !existingCoupon.data) {
      return {
        success: false,
        error: 'Cupón no encontrado',
      }
    }

    // Check if product already exists in coupon
    const productsRef = ref(database, `${COUPONS_BASE_PATH}/${couponName}/products/${codigo}`)
    const productSnapshot = await get(productsRef)

    if (productSnapshot.exists()) {
      return {
        success: false,
        error: 'El producto ya está agregado a este cupón',
      }
    }

    await set(productsRef, product)

    const productInfo = extractProductAuditInfo(product)
    await appendCouponAuditEntry(
      couponName,
      buildProductAuditEntry('product_added', normalizedActor, productInfo)
    )

    return {
      success: true,
      message: 'Producto agregado correctamente',
    }
  } catch (error) {
    console.error('Error in addProductToCoupon:', error)
    return {
      success: false,
      error: error.message || 'Error al agregar producto al cupón',
    }
  }
}

/**
 * Remove a product from a coupon
 * @param {string} couponName - Coupon name
 * @param {string} productCode - Product CODIGO
 * @param {Object} actor - Usuario autenticado (uid, email, name)
 * @param {Object} [product] - Producto asignado (opcional, para enriquecer auditoría)
 * @returns {Promise<Object>} Result of remove operation
 */
export const removeProductFromCoupon = async (couponName, productCode, actor, product = null) => {
  try {
    const actorValidation = validateCouponActor(actor)
    if (!actorValidation.valid) {
      return {
        success: false,
        error: actorValidation.error,
      }
    }

    if (!couponName || !productCode) {
      return {
        success: false,
        error: 'Cupón y código de producto son requeridos',
      }
    }

    const normalizedActor = actorValidation.actor

    const existingCoupon = await getCouponByName(couponName)
    if (!existingCoupon.success || !existingCoupon.data) {
      return {
        success: false,
        error: 'Cupón no encontrado',
      }
    }

    const productRef = ref(database, `${COUPONS_BASE_PATH}/${couponName}/products/${productCode}`)
    const productSnapshot = await get(productRef)

    if (!productSnapshot.exists()) {
      return {
        success: false,
        error: 'El producto no está asignado a este cupón',
      }
    }

    const storedProduct = product || productSnapshot.val()
    const productInfo = storedProduct
      ? extractProductAuditInfo({ ...storedProduct, CODIGO: productCode })
      : { codigo: String(productCode), nombre: String(productCode) }

    await remove(productRef)

    await appendCouponAuditEntry(
      couponName,
      buildProductAuditEntry('product_removed', normalizedActor, productInfo)
    )

    return {
      success: true,
      message: 'Producto eliminado correctamente',
    }
  } catch (error) {
    console.error('Error in removeProductFromCoupon:', error)
    return {
      success: false,
      error: error.message || 'Error al eliminar producto del cupón',
    }
  }
}

/**
 * Subscribe to coupon products changes in real-time
 * @param {string} couponName - Coupon name
 * @param {Function} callback - Callback function to handle updates
 * @returns {Function} Unsubscribe function
 */
export const subscribeToCouponProducts = (couponName, callback) => {
  try {
    const productsRef = ref(database, `${COUPONS_BASE_PATH}/${couponName}/products`)

    const handleSnapshot = snapshot => {
      if (snapshot.exists()) {
        const productsData = snapshot.val()
        // Convert object to array with CODIGO as key
        const productsArray = Object.keys(productsData).map(codigo => ({
          CODIGO: codigo,
          ...productsData[codigo],
        }))
        callback({
          success: true,
          data: productsArray,
        })
      } else {
        callback({
          success: true,
          data: [],
        })
      }
    }

    onValue(productsRef, handleSnapshot)

    // Return unsubscribe function
    return () => {
      off(productsRef, 'value', handleSnapshot)
    }
  } catch (error) {
    console.error('Error in subscribeToCouponProducts:', error)
    callback({
      success: false,
      error: error.message || 'Error al suscribirse a productos del cupón',
      data: [],
    })
    return () => {} // Return empty unsubscribe function
  }
}

/**
 * Get product count for a specific coupon
 * @param {string} couponName - Coupon name
 * @returns {Promise<number>} Product count
 */
export const getCouponProductCount = async couponName => {
  try {
    const productsRef = ref(database, `${COUPONS_BASE_PATH}/${couponName}/products`)
    const snapshot = await get(productsRef)

    if (snapshot.exists()) {
      const productsData = snapshot.val()
      return Object.keys(productsData).length
    } else {
      return 0
    }
  } catch (error) {
    console.error('Error in getCouponProductCount:', error)
    return 0
  }
}
